import { useChartTitle, useReportId } from 'hooks/helperHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import BatterConfig from './BatterConfig';
import BatterSearchCriteria from './BatterSearchCriteria';
import ChartSidebarSummary from 'components/ReportShelf/ChartSidebarSummary';
import PlayerReportHeader from 'components/ReportPageHeader/PlayerReportHeader';
import ReportBar from 'components/ReportBar/ReportBar';
import ReportPage from 'components/ReportPage/ReportPage';
import ReportShelf from 'components/ReportShelf/ReportShelf';
import { Typography } from '@trackman/web-shared-components';
import { chartIdTochartSetup } from 'utilities/chartIdToComponent';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import usePlayerReportBatter from 'hooks/PlayerReport/Batter/usePlayerReportBatter';
import { useTranslation } from 'react-i18next';
import LoadingModal from 'components/LoadingModal/LoadingModal';
import { useChartVisibility } from 'hooks/useChartVisibility';
import { useChartBrokerStore } from 'stores/ChartBrokerStore';

const PlayerReportBatterPage = () => {
  const { isCreate } = useReportId();

  const { t } = useTranslation();

  const {
    isEditMode,
    setIsEditMode,

    searchCriteria,
    searchChips,

    tempLayout,
    chartData,

    isLoadingReport,
    generateReport,
    getNewData,
    setTempLayout,

    saveLayoutForSession,
    saveLayoutForOrg,

    resetStore,
    editingChart,
    setEditingChart,

    hasUnsavedEdits,
    discardLayoutChanges,
  } = usePlayerReportBatter();

  const [openSection, setOpenSection] = useState<'searchCriteria' | 'config'>(isCreate ? 'searchCriteria' : 'config');
  const [parent] = useAutoAnimate();

  const downloadName = useMemo(() => {
    const { playerNames = [], seasonNames = [] } = searchChips;
    const playerStr = playerNames.length > 0 ? ` - ${playerNames.slice(0, 3).join('; ')}${playerNames.length > 3 ? '...' : ''}` : '';
    const timeStr = seasonNames.length > 0 ? `${seasonNames.slice(0, 3).join(', ')} ` : '';
    return `${timeStr}Batter Report${playerStr}`;
  }, [searchChips.playerNames, searchChips.seasonNames]);

  const { reset: resetChartBrokerStore } = useChartBrokerStore();

  useEffect(() => {
    return () => {
      resetStore();
      resetChartBrokerStore();
    };
  }, [resetStore, resetChartBrokerStore]);

  const { getChartTitle, getChartStats } = useChartTitle();

  const { visibleCharts, hiddenCharts, toggleChartVisibility } = useChartVisibility(tempLayout, setTempLayout);

  const handleGenerate = useCallback(() => {
    setOpenSection('config');
    generateReport();
  }, [generateReport]);

  const handleToggleVisibility = useCallback(
    (chartId: string) => {
      toggleChartVisibility(chartId);
    },
    [toggleChartVisibility]
  );

  return (
    <>
      <ReportBar
        reportName={t('PlayerReport.Batter')}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        downloadName={downloadName}
        saveLayoutForSession={saveLayoutForSession}
        saveLayoutForOrg={saveLayoutForOrg}
        hasUnsavedEdits={hasUnsavedEdits}
        discardLayoutChanges={discardLayoutChanges}
      />

      <div className='report-layout-content'>
        <ReportShelf
          onGenerate={handleGenerate}
          onModify={getNewData}
          isLoadingReport={isLoadingReport}
          disableGenerate={isLoadingReport || !searchCriteria.players || searchCriteria.players.length === 0}
          editingChart={editingChart}
          setEditingChart={setEditingChart}
          isEditMode={isEditMode}
        >
          <div className='report-layout-shelf' ref={parent}>
            {tempLayout && isEditMode ? (
              <>
                <div className='report-layout-shelf-section'>
                  {!editingChart && (
                    <Typography component='div' variant='body bold'>
                      {t('Report elements')}
                    </Typography>
                  )}
                  {visibleCharts.map((config) => {
                    const { chartId, chartDrawingInfo, ...extraChartData } = config;
                    const { Sidebar } = chartIdTochartSetup[chartId];
                    if (!editingChart) {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={Sidebar ? setEditingChart : undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartStats(config.chartId, config)}
                          isHidden={config.isHidden ?? false}
                        />
                      );
                    }
                    return Sidebar && editingChart === chartId ? (
                      <Sidebar
                        extraChartData={extraChartData as never}
                        setTempLayout={setTempLayout as any}
                        tempLayout={tempLayout as any}
                      />
                    ) : null;
                  })}
                </div>
                {!editingChart && hiddenCharts.length > 0 && (
                  <div className='report-layout-shelf-section'>
                    <Typography component='div' variant='body bold'>
                      {t('Extra elements')}
                    </Typography>
                    {hiddenCharts.map((config) => {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartStats(config.chartId, config)}
                          isHidden={true}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <BatterSearchCriteria
                  isSearchCriteriaOpen={openSection === 'searchCriteria'}
                  setSearchCriteriaOpen={() => setOpenSection('searchCriteria')}
                />
                <BatterConfig isConfigOpen={openSection === 'config'} setConfigOpen={() => setOpenSection('config')} />
              </>
            )}
          </div>
        </ReportShelf>
        <div className='report-layout-grid-container'>
          {tempLayout &&
            (chartData ?? []).map((chart, i) => (
              <ReportPage
                key={i}
                layout={tempLayout}
                page={i + 1}
                header={
                  <PlayerReportHeader
                    playerName={chart.playerName}
                    teamName={searchChips.teamName}
                    seasonNames={searchChips.seasonNames}
                    dateRanges={searchChips.dateRanges}
                  />
                }
                pageChartData={chart}
                isEditMode={isEditMode}
                isLoadingData={isLoadingReport}
                setTempLayout={setTempLayout}
                setEditingChart={setEditingChart}
                editingChart={editingChart}
              />
            ))}
          {tempLayout && (chartData == null || chartData.length === 0) && (
            <ReportPage
              layout={tempLayout}
              page={1}
              header={<PlayerReportHeader />}
              pageChartData={undefined}
              isEditMode={isEditMode}
              isLoadingData={isLoadingReport}
              setTempLayout={setTempLayout}
              setEditingChart={setEditingChart}
              editingChart={editingChart}
            />
          )}
        </div>
      </div>

      <LoadingModal isLoadingReport={isLoadingReport} />
    </>
  );
};

export default PlayerReportBatterPage;
